import { doc, getDoc } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { shuttleData } from '../data/shuttleData'
import { SHUTTLE_STOPS, ROUTE_STOP_MAP, SHUTTLE_SCHEDULES } from '../data/shuttle'
import { validateShuttleData } from '../utils/validateShuttleData'

export const SHUTTLE_CACHE_KEY = 'suguide_shuttle_published_cache_v1'
export const SHUTTLE_DRAFT_DOC = ['adminContent', 'shuttleDraft']
export const SHUTTLE_PUBLISHED_DOC = ['adminContent', 'shuttlePublished']
export const SHUTTLE_VERSION_COLLECTION = ['adminContent', 'shuttleVersions', 'items']

function clean(value) {
  return JSON.parse(JSON.stringify(value ?? null))
}

function toStopArray(stopsObj = SHUTTLE_STOPS) {
  return Object.values(stopsObj).map(stop => ({
    ...clean(stop),
    category: stop.category ?? stop.type ?? '',
    memo: stop.memo ?? stop.detail ?? stop.description ?? '',
    active: stop.active !== false,
  }))
}

function toRoutesAndSchedules(routes = shuttleData.routes ?? []) {
  const routesOut = []
  const schedulesOut = []
  for (const route of routes) {
    const routeId = route.routeId ?? route.id
    const stopMap = ROUTE_STOP_MAP[routeId] ?? {}
    const stopIds = Array.isArray(route.stopIds) ? route.stopIds : (stopMap.stopIds ?? [])
    const firstStop = route.stops?.[0] ?? {}
    routesOut.push({
      id: routeId,
      name: route.routeName ?? route.name ?? '',
      stopIds,
      direction: route.direction ?? (
        String(routeId).startsWith('school_to_') ? 'fromCampus' :
          String(routeId).endsWith('_to_school') ? 'toCampus' : 'loop'
      ),
      fare: route.fare ?? '',
      active: route.active !== false,
      boardingStopId: stopMap.boardingStopId ?? stopIds[0] ?? '',
      alightingStopId: stopMap.alightingStopId ?? stopIds.at(-1) ?? '',
      displayStops: stopMap.displayStops ?? '',
      memo: route.memo ?? firstStop.location ?? '',
    })

    ;(firstStop.times ?? []).forEach((time, index) => {
      schedulesOut.push({
        routeId,
        departureTime: time,
        days: ['mon', 'tue', 'wed', 'thu', 'fri'],
        semesterType: 'regular',
        active: true,
        buses: firstStop.buses?.[index] ?? null,
        busType: firstStop.busTypes?.[index] ?? null,
        charterStation: firstStop.charterStations?.[index] ?? null,
      })
    })
  }
  return { routes: routesOut, schedules: schedulesOut }
}

export function buildLocalShuttleCmsData(extra = {}) {
  const { routes, schedules } = toRoutesAndSchedules()
  return {
    stops: toStopArray(),
    routes,
    schedules,
    status: 'draft',
    source: shuttleData.source ?? 'local',
    lastUpdated: shuttleData.lastUpdated ?? null,
    ...extra,
  }
}

export function normalizeShuttleCmsData(data = {}) {
  return {
    stops: Array.isArray(data.stops) ? data.stops.map(stop => ({
      ...stop,
      id: String(stop.id ?? '').trim(),
      name: String(stop.name ?? '').trim(),
      lat: stop.lat === '' || stop.lat == null ? null : Number(stop.lat),
      lng: stop.lng === '' || stop.lng == null ? null : Number(stop.lng),
      type: stop.type ?? stop.category ?? '',
      category: stop.category ?? stop.type ?? '',
      memo: stop.memo ?? '',
      active: stop.active !== false,
    })) : [],
    routes: Array.isArray(data.routes) ? data.routes.map(route => ({
      ...route,
      id: String(route.id ?? route.routeId ?? '').trim(),
      name: String(route.name ?? route.routeName ?? '').trim(),
      stopIds: Array.isArray(route.stopIds)
        ? route.stopIds.map(id => String(id).trim()).filter(Boolean)
        : String(route.stopIds ?? '').split(',').map(id => id.trim()).filter(Boolean),
      direction: route.direction ?? '',
      active: route.active !== false,
    })) : [],
    schedules: Array.isArray(data.schedules) ? data.schedules.map(schedule => ({
      ...schedule,
      routeId: String(schedule.routeId ?? '').trim(),
      departureTime: String(schedule.departureTime ?? schedule.time ?? '').trim(),
      days: Array.isArray(schedule.days)
        ? schedule.days
        : String(schedule.days ?? '').split(',').map(v => v.trim()).filter(Boolean),
      semesterType: schedule.semesterType ?? 'regular',
      exceptionDates: Array.isArray(schedule.exceptionDates)
        ? schedule.exceptionDates
          .filter(entry => entry && typeof entry.date === 'string' && entry.date.trim())
          .map(entry => ({ date: entry.date.trim(), active: entry.active !== false }))
        : [],
      active: schedule.active !== false,
    })) : [],
  }
}

function buildLegacyRoutes(cmsData) {
  const schedulesByRoute = new Map()
  for (const schedule of cmsData.schedules ?? []) {
    if (schedule.active === false) continue
    const list = schedulesByRoute.get(schedule.routeId) ?? []
    list.push(schedule)
    schedulesByRoute.set(schedule.routeId, list)
  }

  return (cmsData.routes ?? [])
    .filter(route => route.active !== false)
    .map(route => {
      const stopIds = route.stopIds ?? []
      const firstStop = cmsData.stops?.find(stop => stop.id === stopIds[0])
      const schedules = (schedulesByRoute.get(route.id) ?? [])
        .sort((a, b) => String(a.departureTime).localeCompare(String(b.departureTime)))
      return {
        routeId: route.id,
        routeName: route.name,
        fare: route.fare ?? '',
        active: true,
        cmsManaged: true,
        stops: [{
          stopName: firstStop?.name ?? route.name,
          location: route.memo ?? firstStop?.memo ?? firstStop?.detail ?? '',
          times: schedules.map(item => item.departureTime),
          buses: schedules.map(item => item.buses ?? null),
          busTypes: schedules.map(item => item.busType ?? null),
          charterStations: schedules.map(item => item.charterStation ?? null),
          // semesterType이 'regular'는 정규화 기본값이라 구분 신호가 못 된다 — 'vacation' 태그나
          // exceptionDates가 실제로 하나라도 있을 때만(=관리자가 실제로 설정) 채운다. 그렇지 않으면
          // undefined로 남겨 shuttleDateOverrides의 필터링이 완전히 no-op이 되게 한다(로컬 fallback이
          // 방학 기간에 우연히 걸러지는 일이 없어야 한다).
          ...(schedules.some(item => item.semesterType === 'vacation' || (item.exceptionDates ?? []).length > 0) ? {
            semesterTypes: schedules.map(item => item.semesterType || null),
            exceptionDates: schedules.map(item => item.exceptionDates ?? []),
          } : {}),
        }],
      }
    })
}

export function applyShuttleCmsData(data) {
  const normalized = normalizeShuttleCmsData(data)
  const validation = validateShuttleData(normalized)
  if (!validation.ok) return { ok: false, validation }

  const activeStops = normalized.stops.filter(stop => stop.active !== false)
  for (const key of Object.keys(SHUTTLE_STOPS)) delete SHUTTLE_STOPS[key]
  for (const stop of activeStops) {
    SHUTTLE_STOPS[stop.id] = {
      ...stop,
      detail: stop.detail ?? stop.memo ?? '',
      description: stop.description ?? stop.memo ?? '',
      icon: stop.icon ?? (stop.type === 'external' ? '🚉' : '🚌'),
      mapVisible: stop.mapVisible,
    }
  }

  for (const key of Object.keys(ROUTE_STOP_MAP)) delete ROUTE_STOP_MAP[key]
  for (const route of normalized.routes.filter(route => route.active !== false)) {
    ROUTE_STOP_MAP[route.id] = {
      stopIds: route.stopIds,
      boardingStopId: route.boardingStopId ?? route.stopIds?.[0],
      alightingStopId: route.alightingStopId ?? route.stopIds?.at(-1),
      displayStops: route.displayStops ?? route.stopIds?.join(' → ') ?? route.name,
    }
  }

  shuttleData.routes = buildLegacyRoutes(normalized)
  shuttleData.lastUpdated = data.publishedAt ?? data.updatedAt ?? data.lastUpdated ?? shuttleData.lastUpdated
  shuttleData.source = data.source ?? 'Firestore adminContent/shuttlePublished/main'
  shuttleData.confidence = 'firestore_published'
  shuttleData.cmsVersion = data.version ?? null

  SHUTTLE_SCHEDULES.splice(0, SHUTTLE_SCHEDULES.length)
  for (const schedule of normalized.schedules.filter(item => item.active !== false)) {
    const route = normalized.routes.find(item => item.id === schedule.routeId)
    if (!route || route.active === false) continue
    SHUTTLE_SCHEDULES.push({
      id: `${schedule.routeId}_${schedule.departureTime.replace(':', '')}`,
      routeId: schedule.routeId,
      routeName: route.name,
      direction: route.direction,
      departureTime: schedule.departureTime,
      buses: schedule.buses ?? null,
      fare: route.fare ?? '',
      stopIds: route.stopIds ?? [],
      boardingStopId: route.boardingStopId ?? route.stopIds?.[0],
      active: true,
    })
  }

  return { ok: true, validation, data: normalized }
}

export function cacheShuttlePublished(data) {
  try {
    localStorage.setItem(SHUTTLE_CACHE_KEY, JSON.stringify({ cachedAt: Date.now(), data: clean(data) }))
  } catch {}
}

export function loadCachedShuttlePublished() {
  try {
    const parsed = JSON.parse(localStorage.getItem(SHUTTLE_CACHE_KEY) || 'null')
    return parsed?.data ?? null
  } catch {
    return null
  }
}

export async function loadPublishedShuttleData() {
  const cached = loadCachedShuttlePublished()
  if (cached) applyShuttleCmsData(cached)
  if (!db) return { source: cached ? 'cache' : 'local', data: cached }

  const snap = await getDoc(doc(db, ...SHUTTLE_PUBLISHED_DOC))
  if (!snap.exists()) return { source: cached ? 'cache' : 'local', data: cached }
  const data = snap.data()?.main
  const result = applyShuttleCmsData(data)
  if (result.ok) cacheShuttlePublished(data)
  return { source: result.ok ? 'firestore' : 'local', data, validation: result.validation }
}
